import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg, fmtDateTime, num, StatusChip } from './common';

interface Props {
  orderId: number | null;
  onClose: () => void;
  onOpenPo?: (poId: number) => void;
}

/** A whole customer order and, for every item, where it is on the PO side (or not ordered yet). */
const CustomerOrderDialog: React.FC<Props> = ({ orderId, onClose, onOpenPo }) => {
  const [order, setOrder] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = (id: number) => orderProcessorAPI.getCustomerOrder(id).then(setOrder).catch((e) => setError(errMsg(e)));

  useEffect(() => {
    if (!orderId) return;
    setOrder(null); setError(null);
    reload(orderId);
  }, [orderId]);

  // Customer orders are no longer flipped automatically when a PO is sent — staff decide here
  const markOrdered = async () => {
    if (!orderId || !order) return;
    const notOnPo = (order.items || []).filter((i: any) => !i.po_lines?.length).length;
    if (!window.confirm(notOnPo
      ? `${notOnPo} item(s) of this order are not on a PO yet. Mark the whole order as Ordered anyway? It will leave the Commitment page.`
      : 'Mark this customer order as Ordered? It will leave the Commitment page.')) return;
    setBusy(true); setError(null);
    try { await orderProcessorAPI.markCustomerOrderOrdered(orderId); await reload(orderId); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const items: any[] = order?.items || [];
  const onPo = items.filter((i) => i.po_lines?.length).length;

  return (
    <Dialog open={!!orderId} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Customer order #{orderId} {order && <StatusChip status={order.status} />}
        {order && (
          <Typography variant="body2" color="text.secondary">
            {order.customer_name || '—'}{order.customer_number ? ` · ${order.customer_number}` : ''} · {order.store_name} ·
            {' '}{order.date ? String(order.date).slice(0, 10) : '—'} · taken by {order.staff_name || '—'}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!order && !error && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
        {order && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
              <Typography variant="body2"><b>{items.length}</b> item(s)</Typography>
              <Typography variant="body2" color="success.main"><b>{onPo}</b> on a PO</Typography>
              {items.length - onPo > 0 && <Typography variant="body2" color="warning.main"><b>{items.length - onPo}</b> not on a PO yet</Typography>}
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Medicine</TableCell>
                    <TableCell>Pack</TableCell>
                    <TableCell align="right">Ordered (strips)</TableCell>
                    <TableCell>PO</TableCell>
                    <TableCell>Distributor</TableCell>
                    <TableCell>PO status</TableCell>
                    <TableCell>Line status</TableCell>
                    <TableCell align="right">PO qty (units)</TableCell>
                    <TableCell align="right">Received</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it: any) => {
                    const lines: any[] = it.po_lines || [];
                    if (!lines.length) {
                      return (
                        <TableRow key={it.id} sx={{ bgcolor: '#fff8e1' }}>
                          <TableCell><Typography variant="body2" fontWeight={500}>{it.medicine_name}</Typography><Typography variant="caption" color="text.secondary">{it.manufacturer_name}</Typography></TableCell>
                          <TableCell>{it.pack_size || '—'}</TableCell>
                          <TableCell align="right">{num(it.quantity, 0)}</TableCell>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="warning.main">
                              {it.pending_runs?.length
                                ? `Not on a PO yet — in run ${it.pending_runs.map((r: any) => `${r.run_number} (${r.decision})`).join(', ')}`
                                : 'Not on a PO yet'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return lines.map((l: any, idx: number) => (
                      <TableRow key={`${it.id}-${l.line_id}`}>
                        {idx === 0 && (
                          <>
                            <TableCell rowSpan={lines.length}><Typography variant="body2" fontWeight={500}>{it.medicine_name}</Typography><Typography variant="caption" color="text.secondary">{it.manufacturer_name}</Typography></TableCell>
                            <TableCell rowSpan={lines.length}>{it.pack_size || '—'}</TableCell>
                            <TableCell rowSpan={lines.length} align="right">{num(it.quantity, 0)}</TableCell>
                          </>
                        )}
                        <TableCell>
                          {onOpenPo ? <Button size="small" onClick={() => onOpenPo(l.po_id)}>{l.po_number}</Button> : l.po_number}
                          <Typography variant="caption" display="block" color="text.secondary">{l.run_number}{l.sent_at ? ` · sent ${fmtDateTime(l.sent_at)}` : ''}</Typography>
                        </TableCell>
                        <TableCell>{l.distributor_name}</TableCell>
                        <TableCell><StatusChip status={l.po_status} /></TableCell>
                        <TableCell><StatusChip status={l.line_status} /></TableCell>
                        <TableCell align="right">{num(l.loose_qty, 0)}<Typography variant="caption" display="block" color="text.secondary">{num(l.strip_qty, 0)} strips</Typography></TableCell>
                        <TableCell align="right">{num(l.received_qty, 0)}</TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {order?.status === 'pending' && (
          <Button variant="contained" color="secondary" disabled={busy} onClick={markOrdered}
            startIcon={busy ? <CircularProgress size={16} /> : undefined}>Mark as Ordered</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerOrderDialog;
